import React, { useRef, useState, useLayoutEffect } from 'react';
import { sendTrackingInfo, useOutsideClick } from '@panel/commons';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { select } from '@panel/commons/core/themev2';
import { isPortalWithoutArchiveOption } from '@panel/commons/helpers';
import { isBrasilSite } from '@panel/commons/helpers/siteUtils';
import SimpleTooltip from '@panel/commons/lib/Tooltip/SimpleTooltip';
import SimpleTag from '@panel/commons/lib/SimpleTag';
import OptionsSelector from '@panel/commons/lib/DropDown/OptionSelector';
import Statistics from '@panel/commons/assets/icons/Statistics.svg';
import ArchiveIcon from '@panel/commons/assets/icons/archive.svg';
import DesempenoIcon from '@panel/commons/assets/icons/desempenoIcon.svg';
import FlagIcon from '@panel/commons/assets/icons/flag.svg';
import LockIcon from '@panel/commons/assets/icons/lock.svg';
import PowerIcon from '@panel/commons/assets/icons/powerButtonRed.svg';
import TrashIcon from '@panel/commons/assets/icons/trashIconRed.svg';
import TrendingUpIcon from '@panel/commons/assets/icons/trendingUp.svg';
import StatisticsIcon from '@panel/commons/assets/icons/icono_estadisticas.svg';
import StatisticsIconDisabled from '@panel/commons/assets/icons/profits-disabled.svg';
import Clock from '@panel/commons/assets/icons/clock.svg';
import PlayIcon from '../../../../assets/icons/PlayIcon.svg';
import PlayIconDisabled from '../../../../assets/icons/PlayIconDisabled.svg';
import PlayIconHover from '../../../../assets/icons/PlayIconHover.svg';
import PostingIcon from '../../../../assets/icons/PostingIcon.svg';
import PostingIconHover from '../../../../assets/icons/PostingIconHover.svg';
import EditIcon from '../../../../assets/icons/EditIcon.svg';
import EditIconHover from '../../../../assets/icons/EditIconHover.svg';
import EditIconDisabled from '../../../../assets/icons/EditIconDisabled.svg';
import OptionsIcon from '../../../../assets/icons/OptionsIcon.svg';
import OptionsIconHover from '../../../../assets/icons/OptionsIconHover.svg';
import Report from '../../../../assets/icons/social-report.svg';
import SocialActivate from '../../../../assets/icons/activate-socialads.svg';
import Speaker from '../../../../assets/icons/speaker.svg';
import Print from '../../../../../commons/assets/icons/print.svg';

const ActionsArea = ({
  device,
  isOpenThisMenu,
  setMenuOpenId,
  isOnline,
  openModal,
  lockedSoft,
  redirectToEdit,
  isDraft,
  userCanEditPermissions,
  isDeleted,
  linkToPostingUrl,
  postingId,
  showingModal,
  isReservado,
  handleCancelReservation,
  handleReservation,
  usedForTour,
  isIntegrator,
  withoutCreditState,
  isAssistant,
  finishReason,
  hasSelectorPosting,
  setActiveShadow,
  activeShadow,
  setShowSelectPosting,
  showSelectPosting,
  handleOutsideClick,
  optionsPostingsArray
}) => {
  const { t } = useTranslation();
  const hasFinishAction = isOnline || withoutCreditState;
  const positionRef = useRef(null);
  const positionSelectRef = useRef(null);
  const [bottomOpen, setBottomOpen] = useState(false);
  const [hoverIcon, setHoverIcon] = useState(null);
  const [upPosition, setUpPosition] = useState(false);
  const postingWithoutArchiveOption = isPortalWithoutArchiveOption() && isOnline;
  const hasArchiveOption = !isDeleted && !isAssistant && !postingWithoutArchiveOption;
  const hasWrongFinishReason =
    finishReason && finishReason.reason && (finishReason.reason === 'fraud' || finishReason.reason === 'infringement');
  const disabledActionsMenu = isDraft && !userCanEditPermissions;

  useOutsideClick(positionRef, () => {
    if (isOpenThisMenu) {
      setMenuOpenId(null);
      setActiveShadow(false);
    }
  });

  const handleEditRedirect = (e, openInNewTab) => {
    sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Editar');
    if (isIntegrator) {
      openModal(e, 'integratorModal');
    } else {
      redirectToEdit(openInNewTab);
    }
  };

  const linkToEstadisticas = `../panel/desempeno?postingId=${postingId}&show=statistics`;
  const linkToEditPermissions = `../panel/contactos-permisos?postingId=${postingId}`;

  // eslint-disable-next-line consistent-return
  const onMouseDownHandler = e => {
    const buttonId = e.button;
    // se verifica si el click se hace al mismo tiempo con el press de la tecla ctrl
    const withCtrlPress = buttonId === 0 && e.ctrlKey;
    // se verifica si el click es en la rueda de scroll del mouse
    const isWheelPress = buttonId === 1;
    if (isWheelPress || withCtrlPress) {
      return handleEditRedirect(e, true);
    }
    if (buttonId === 0) {
      handleEditRedirect(e, false);
    }
  };

  const textTooltip = {
    publish: {
      regularText: !isOnline ? 'postings.card.detailAction.publish' : 'panel.avisos.posting.active.publish',
      fraud: 'postings.card.OptionsMenu.pusblish.fraud',
      infringement: 'postings.card.OptionsMenu.pusblish.infringement'
    },
    goToEdit: {
      regularText: 'postings.card.OptionsMenu.linkToEdit',
      fraud: 'postings.card.OptionsMenu.goToEdit.fraud',
      infringement: 'postings.card.OptionsMenu.goToEdit.infringement'
    },
    goToPosting: {
      regularText: 'postings.card.OptionsMenu.goToPosting'
    },
    moreOptions: {
      regularText: 'postings.card.OptionsMenu.moreOptions'
    }
  };

  const getHighTooltip = () => {
    if (isBrasilSite && finishReason && finishReason.reason && finishReason.reason === 'infringement') return '58px';
    if (hasWrongFinishReason) return '39px';
    return null;
  };

  const getWidthTooltip = () => {
    if (isBrasilSite && hasWrongFinishReason) return '171px';
    if (hasWrongFinishReason) return '168px';
    return null;
  };

  useLayoutEffect(() => {
    // Efecto para posicionar 'hacia arriba' o 'hacia abajo' Menú de más opciones:
    const positionDif =
      positionRef.current &&
      positionRef.current.getBoundingClientRect().top - 70 - positionRef.current.getBoundingClientRect().height;
    setBottomOpen(positionDif <= 0);
  }, [isOpenThisMenu]);

  useLayoutEffect(() => {
    // Efecto para posicionar 'hacia arriba' o 'hacia abajo' Selector para ir al aviso (en caso avisos clonados):
    const positionBottom = positionSelectRef.current && positionSelectRef.current.getBoundingClientRect().bottom;
    setUpPosition(positionBottom + 5 >= window.innerHeight);
  }, [showSelectPosting.onActionButton]);

  return (
    <Container>
      {!isAssistant && (
        <>
          <SimpleTooltip
            isMobile={device === 'tablet'}
            width={getWidthTooltip()}
            height={getHighTooltip()}
            whiteSpace={hasWrongFinishReason ? 'break-spaces' : null}
            position='top-center'
            text={t(hasWrongFinishReason ? textTooltip.publish[finishReason.reason] : textTooltip.publish.regularText)}
            dataPanel='info-rePost'
          >
            <ButtonContainer
              data-tour-panel={usedForTour && 'quick-actions-0'}
              data-panel='posting-action-publish'
              onMouseEnter={() => {
                if (!hasWrongFinishReason) setHoverIcon('Play');
              }}
              onMouseLeave={() => setHoverIcon(null)}
              isDisabled={hasWrongFinishReason}
            >
              <CustomLink
                isDisabled={hasWrongFinishReason}
                onClick={e => {
                  if (!hasWrongFinishReason) sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Publicar aviso');
                  if (lockedSoft && !hasWrongFinishReason) return openModal(e, 'lockedSoft');
                  return !hasWrongFinishReason ? openModal(e, 'activationModal') : null;
                }}
              >
                <ActionIcon isHover={hoverIcon === 'Play'} icon='Play' isDisabled={hasWrongFinishReason} />
              </CustomLink>
            </ButtonContainer>
          </SimpleTooltip>
          <div className='separator' />
        </>
      )}
      {!isAssistant && (
        <>
          <SimpleTooltip
            isMobile={device === 'tablet'}
            width={getWidthTooltip()}
            height={getHighTooltip()}
            whiteSpace={hasWrongFinishReason ? 'break-spaces' : null}
            position='top-center'
            text={t(
              hasWrongFinishReason ? textTooltip.goToEdit[finishReason.reason] : textTooltip.goToEdit.regularText
            )}
            dataPanel='info-editPost'
          >
            <ButtonContainer
              data-tour-panel={usedForTour && 'quick-actions-1'}
              data-panel='posting-action-edit'
              onMouseEnter={() => {
                if (!hasWrongFinishReason) setHoverIcon('Edit');
              }}
              onMouseLeave={() => setHoverIcon(null)}
              isDisabled={hasWrongFinishReason}
            >
              <CustomLink
                isDisabled={hasWrongFinishReason}
                onMouseDown={!hasWrongFinishReason ? onMouseDownHandler : null}
              >
                <ActionIcon isHover={hoverIcon === 'Edit'} icon='Edit' isDisabled={hasWrongFinishReason} />
              </CustomLink>
            </ButtonContainer>
          </SimpleTooltip>
          <div className='separator' />
        </>
      )}
      <SimpleTooltip
        position='top-center'
        isMobile={device === 'tablet'}
        text={t(textTooltip.goToPosting.regularText)}
        dataPanel='info-goToPost'
        forceHide={showSelectPosting.onActionButton}
      >
        <ButtonContainer
          data-tour-panel={usedForTour && 'quick-actions-2'}
          onClick={e => {
            e.stopPropagation();
            if (!hasSelectorPosting) sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Ir al aviso');
            if (hasSelectorPosting) {
              setShowSelectPosting({ onActionButton: !showSelectPosting.onActionButton });
              if (!showSelectPosting.onImage && !showSelectPosting.onTitle) {
                setActiveShadow(!activeShadow);
              }
              setMenuOpenId(null);
            }
          }}
          data-panel='posting-action-irAviso'
          onMouseEnter={() => setHoverIcon('Preview')}
          onMouseLeave={() => setHoverIcon(null)}
          activeBackground={showSelectPosting.onActionButton}
        >
          <StyledLink href={!hasSelectorPosting ? linkToPostingUrl : null} target='_blank' rel='noopener noreferrer'>
            <ActionIcon isHover={hoverIcon === 'Preview' || showSelectPosting.onActionButton} icon='Preview' />
          </StyledLink>
        </ButtonContainer>
        {hasSelectorPosting && showSelectPosting.onActionButton && (
          <OptionsSelector
            isOpen={showSelectPosting.onActionButton}
            optionsArray={optionsPostingsArray}
            elementRef={positionSelectRef}
            handleOutsideClick={handleOutsideClick}
            getTopPosition={upPosition}
            width='171px'
            height='94px'
            topUp='43px'
            topDown='178px'
            right='58px'
            left='unset'
          />
        )}
      </SimpleTooltip>
      {!disabledActionsMenu && (
        <>
          <div className='separator' />
          <SimpleTooltip
            isMobile={device === 'tablet'}
            forceHide={isOpenThisMenu || showingModal}
            text={t(textTooltip.moreOptions.regularText)}
            position='top-center'
            dataPanel='info-moreOptions'
          >
            <ButtonContainer
              data-tour-panel={usedForTour && 'quick-actions-3'}
              onClick={e => {
                e.stopPropagation();
                setMenuOpenId(isOpenThisMenu ? null : postingId);
                setShowSelectPosting({ onImage: false, onTitle: false, onActionButton: false });
              }}
              data-panel='posting-action-menu'
              onMouseEnter={() => setHoverIcon('Options')}
              onMouseLeave={() => setHoverIcon(null)}
            >
              <ActionIcon
                isHover={hoverIcon === 'Options'}
                className='ADDITIONAL-ACTIONS'
                icon='Ellipsis'
                isSelected={isOpenThisMenu}
              >
                {isOpenThisMenu && (
                  <AdditionalActionsMenu ref={positionRef} position={bottomOpen}>
                    <MoreActionsSection
                      title={t('postings.card.OptionsMenu.moreOptions.manageSection')}
                      withoutSeparator
                    >
                      {userCanEditPermissions && (
                        <div className='listElement'>
                          <ActionLink
                            data-panel='posting-action-menu-editPermisos'
                            href={linkToEditPermissions}
                            rel='noopener noreferrer'
                            icon='lock'
                            onClick={() => {
                              sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Contactos Dropdown');
                            }}
                          >
                            {t('postings.card.OptionsMenu.editPermissions')}
                            <SimpleTag text={t('panel.desempeno.tips.nuevo')} />
                          </ActionLink>
                        </div>
                      )}
                      {!isDraft && (
                        <ActionLink
                          data-panel='posting-action-menu-estadisticas'
                          // eslint-disable-next-line no-script-url
                          href={linkToEstadisticas}
                          rel='noopener noreferrer'
                          icon='statistics'
                          onClick={() => {
                            sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Estadisticas Dropdown');
                          }}
                        >
                          {t('postings.card.detailAction.statistics')}
                        </ActionLink>
                      )}
                    </MoreActionsSection>
                    <MoreActionsSection
                      title={t('postings.card.OptionsMenu.moreOptions.auditSection')}
                      withoutSeparator={isDraft}
                    >
                      {!isDraft && (
                        <div
                          className='listElement'
                          onClick={e => {
                            if (!isDraft) {
                              openModal(e, 'historyModal');
                              sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Ver Historial Dropdown');
                            }
                          }}
                        >
                          <AdditionalAction data-panel='posting-action-menu-historial' icon='History'>
                            {t('postings.card.OptionsMenu.history')}
                          </AdditionalAction>
                        </div>
                      )}
                    </MoreActionsSection>
                    <MoreActionsSection
                      title={t('postings.card.OptionsMenu.moreOptions.actionsSection')}
                      withoutSeparator={isDraft && !userCanEditPermissions}
                    >
                      {isOnline &&
                        !isAssistant &&
                        (!isReservado ? (
                          <div
                            className='listElement'
                            onClick={e => {
                              sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Marcar como reservado Dropdown');
                              handleReservation(e);
                            }}
                          >
                            <AdditionalAction data-panel='posting-action-menu-marcarReservado' icon='flag'>
                              Marcar como reservado
                            </AdditionalAction>
                          </div>
                        ) : (
                          <div
                            className='listElement'
                            onClick={e => {
                              sendTrackingInfo(
                                'Panel',
                                'Mis avisos',
                                'Card del aviso-Desmarcar como reservado Dropdown'
                              );
                              handleCancelReservation(e);
                            }}
                          >
                            <AdditionalAction data-panel='posting-action-menu-desmarcarReservado' icon='flag'>
                              Desmarcar como reservado
                            </AdditionalAction>
                          </div>
                        ))}
                      {hasArchiveOption && (
                        <div
                          className='listElement'
                          onClick={e => {
                            sendTrackingInfo(
                              'Panel',
                              'Mis avisos',
                              `Card del aviso-${isDraft ? 'Descartar' : 'Archivar'} Dropdown`
                            );
                            openModal(e, 'archiveModal');
                          }}
                        >
                          <AdditionalAction
                            data-panel='posting-action-menu-archivar'
                            icon={isDraft ? 'trash' : 'archive'}
                            typeColor={isDraft && 'end'}
                          >
                            {t(`postings.card.detailAction.${isDraft ? 'discard' : 'file'}`)}
                          </AdditionalAction>
                        </div>
                      )}
                      {hasFinishAction && !isAssistant && (
                        <div
                          className='listElement'
                          onClick={e => {
                            openModal(e, 'endModal');
                            sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Finalizar aviso Dropdown');
                          }}
                        >
                          <AdditionalAction data-panel='posting-action-menu-finalizar' icon='power' typeColor='end'>
                            {t('postings.card.detailAction.suspendPosting')}
                          </AdditionalAction>
                        </div>
                      )}
                    </MoreActionsSection>
                  </AdditionalActionsMenu>
                )}
              </ActionIcon>
            </ButtonContainer>
          </SimpleTooltip>
        </>
      )}
    </Container>
  );
};

const MoreActionsSection = ({ title, withoutSeparator = false, children }) => {
  if (!children || (Array.isArray(children) && !children.some(elem => !!elem))) return <></>;
  return (
    <MoreActionsSectionStyled>
      {!withoutSeparator && <div className='separatorSection' />}
      <div className='titleSection'>{title}</div>
      {children}
    </MoreActionsSectionStyled>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  height: inherit;
  .separator {
    width: 1px;
    height: 14px;
    background-color: ${select('grayMedium')};
  }
  .postingFull {
    margin-right: 12px;
    margin-left: 24px;
    font-family: Hind;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
  .rented-tag {
    height: 24px;
    padding: 4px 8px;
    margin-right: 24px;
    font-family: Hind;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    border-radius: 4px;
    border: 1px solid ${select('neutralMediumGray')};
    background-color: ${select('bgBanneri24Full')};
    span {
      color: ${select('orange')};
      font-weight: 600;
    }
  }
`;
const ButtonContainer = styled.div`
  cursor: ${({ isDisabled }) => (!isDisabled ? 'pointer' : 'auto')};
  margin: 0px 3px;
  height: 32px;
  width: 32px;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ activeBackground }) => select(activeBackground && 'grayLight')};
  :hover {
    background-color: ${({ isDisabled }) => select(!isDisabled ? 'grayLight' : null)};
  }
`;

const StyledLink = styled.a`
  display: flex;
  text-decoration: none;
  &:focus,
  &:hover,
  &:visited,
  &:link,
  &:active {
    text-decoration: none;
    color: ${select('blue')};
  }
  margin-bottom: 4px;
  cursor: pointer;
`;

const CustomLink = styled.a`
  display: flex;
  color: ${select('blue')};
  margin-bottom: 4px;
  cursor: ${({ isDisabled }) => (!isDisabled ? 'pointer' : 'auto')};
`;

const Icons = {
  Edit: {
    background: EditIcon,
    backgroundHover: EditIconHover,
    backgroundDisabled: EditIconDisabled,
    width: '16px',
    height: '16px',
    bottom: '-1px'
  },
  Preview: {
    background: PostingIcon,
    backgroundHover: PostingIconHover,
    width: '18px',
    height: '18px',
    bottom: '-1px'
  },
  Statistics: {
    background: Statistics,
    width: '20px',
    height: '20px',
    bottom: '-1px'
  },
  Ellipsis: {
    background: OptionsIcon,
    backgroundHover: OptionsIconHover,
    width: '20px',
    height: '20px',
    bottom: '0px'
  },
  Play: {
    background: PlayIcon,
    backgroundHover: PlayIconHover,
    backgroundDisabled: PlayIconDisabled,
    width: '20px',
    height: '20px',
    bottom: '-1.5px'
  },
  History: {
    background: Clock,
    backgroundHover: Clock,
    width: '20px',
    height: '20px',
    bottom: '0'
  }
};

const getBackgroundIcon = (isDisabled, isHover) => {
  if (isDisabled) return 'backgroundDisabled';
  if (isHover) return 'backgroundHover';
  return 'background';
};

const ActionIcon = styled.div`
  display: inline-block;
  position: relative;
  bottom: ${props => (Icons[props.icon] ? Icons[props.icon].bottom : '0px')};
  height: ${props => (Icons[props.icon] ? Icons[props.icon].height : '10px')};
  width: ${props => (Icons[props.icon] ? Icons[props.icon].width : '10px')};
  background-color: ${({ isSelected }) => isSelected && select('grayLight')};
  background-image: ${props => {
    if (Icons[props.icon]) return `url(${Icons[props.icon][getBackgroundIcon(props.isDisabled, props.isHover)]})`;
    return null;
  }};
  background-repeat: no-repeat;
  background-size: auto;
  background-position: center;
  &.ADDITIONAL-ACTIONS {
    display: flex;
    flex-direction: column;
    position: relative;
    justify-content: center;
    height: inherit;
    width: inherit;
    border-radius: inherit;
  }
`;

const AdditionalActionsMenu = styled.div`
  border: 1px solid ${select('midLightGray')};
  border-radius: 8px;
  width: 314px;
  height: fit-content;
  position: absolute;
  padding: 13px 0 10px;
  background-color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 2;
  cursor: auto;
  right: 35px;
  ${({ position }) =>
    css`
      ${position ? 'top' : 'bottom'}: 0px
    `};
`;

const MoreActionsSectionStyled = styled.div`
  display: flex;
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  height: fit-content;
  width: 100%;
  font-family: 'Hind';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  .titleSection {
    margin-bottom: 2px;
    padding-left: 10px;
    font-size: 12px;
    line-height: 19px;
    color: ${select('darkGray')};
    cursor: auto;
  }
  .listElement {
    height: 37px;
    width: 100%;
    display: flex;
    align-items: center;
    position: relative;
    cursor: pointer;
    :hover {
      background-color: ${select('lightGray')};
    }
  }
  .separatorSection {
    height: 0.5px;
    width: 292px;
    background-color: ${select('midLightGray')};
    margin: 6px 10px 15px;
  }
`;

const additionalActionsIcons = {
  archive: ArchiveIcon,
  desempeno: DesempenoIcon,
  flag: FlagIcon,
  lock: LockIcon,
  play: PlayIcon,
  power: PowerIcon,
  trash: TrashIcon,
  trendingUp: TrendingUpIcon,
  statistics: StatisticsIcon,
  report: Report,
  socialActivate: SocialActivate,
  speaker: Speaker,
  History: Clock,
  Print,
  statisticsDisabled: StatisticsIconDisabled,
  preview: PostingIconHover
};

const AdditionalAction = styled.div`
  display: inline;
  padding-left: 10px;
  color: ${({ typeColor }) => typeColor === 'end' && select('negative')};
  position: absolute;
  top: 8px;
  left: 30px;
  ::before {
    position: absolute;
    top: -2px;
    left: -20px;
    content: ${({ icon }) => `url(${additionalActionsIcons[icon]})`};
    width: 20px;
    height: 22px;
    vertical-align: middle;
  }
`;

const ActionLink = styled.a`
  text-decoration: none;
  color: black;
  padding-left: 10px;
  height: 37px;
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  ::before {
    content: '';
    display: inline-block;
    background-image: ${({ icon, disabled }) =>
      `url(${additionalActionsIcons[`${icon}${disabled ? 'Disabled' : ''}`]})`};
    background-size: 20px 22px;
    width: 20px;
    height: 22px;
    vertical-align: middle;
    margin: 0 10px 4px 0;
  }
  :hover {
    background-color: ${select('lightGray')};
  }
  ${({ disabled }) =>
    disabled &&
    css`
      color: ${select('midLightGray')};
      cursor: default;
      :hover {
        background-color: transparent;
      }
    `}
`;

export default ActionsArea;
